<template>
  <div
    v-if="showComponent"
    :class="['overview-element column small-12', columnsClass]"
    data-test-overview-element-outer-wrapper
  >
    <div>
      <h2 class="element-title no-margin" v-html="element.navigationListBaseModel.title" />
    </div>

    <div class="images-container">
      <!-- Desktop Image -->
      <div class="desktop-image-container hide-for-small-only" :style="desktopImage" />

      <!-- Mobile Image -->
      <div class="mobile-image-container show-for-small-only" :style="backgroundImage" />

      <!-- Thumbnail Image -->
      <div v-if="showThumbnailImage" class="thumbnail-container">
        <img class="thumbnail-image" :src="element.navigationListBaseModel.thumbnailImage.url" />
      </div>
    </div>

    <div class="row link-row">
      <a
        class="column secondary-link small-6"
        :href="element.secondaryLink.url"
        data-test-secondary-link
        @click="dispatchTrackingClickEvent(element.secondaryLink.text)"
      >
        <div class="row align-center">
          <p v-html="element.secondaryLink.text" />
          <span>&gt;</span>
        </div>
      </a>

      <a
        class="column primary-link small-6"
        :href="element.navigationListBaseModel.link.url"
        data-test-primary-link
        @click="dispatchTrackingClickEvent(element.navigationListBaseModel.link.text)"
      >
        <div class="row align-center">
          <p class="primary-link-text" v-html="element.navigationListBaseModel.link.text" />
          <span>&gt;</span>
        </div>
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import NavigationListOverviewElement from '../interfaces/NavigationListOverviewElement';

export default defineComponent({
  name: 'NavigationListOverviewElement',
  props: {
    element: {
      type: Object as PropType<NavigationListOverviewElement>,
      default() {
        return {
          navigationListBaseModel: {
            title: '',
            thumbnailImage: null,
            link: null
          },
          croppedImage: {
            mainImage: null,
            mobileImage: null
          },
          secondaryLink: null,
          elementTypeEnum: 2
        } as NavigationListOverviewElement;
      }
    },
    description: {
      type: String,
      default() {
        return '';
      }
    },
    columnsClass: {
      type: String,
      default() {
        return 'medium-3';
      }
    }
  },
  computed: {
    showComponent(): Boolean {
      return (
        this.element.navigationListBaseModel.link !== null &&
        this.element.secondaryLink !== null &&
        this.element.croppedImage.mainImage !== null &&
        this.element.croppedImage.mobileImage !== null
      );
    },
    backgroundImage(): String {
      return `background-image: url("${this.element.croppedImage.mobileImage?.url}")`;
    },
    desktopImage(): String {
      return `background-image: url("${this.element.croppedImage.mainImage?.url}")`;
    },
    showThumbnailImage(): Boolean {
      return this.element.navigationListBaseModel.thumbnailImage !== null;
    }
  },
  methods: {
    dispatchTrackingClickEvent(linkName: String) {
      this.$store.dispatch('Events/register', {
        event: 'navigation-list-click-overview-element',
        id: 'navigationList',
        vm: {
          label: `${this.element.navigationListBaseModel.title} - ${linkName}`
        }
      });
    }
  }
});
</script>

<style lang="scss" scoped>
@import 'node_modules/foundation-sites/scss/util/util';
@import 'src/styles/all.scss';

.overview-element {
  min-width: rem-calc(305);
  margin-bottom: rem-calc(30);

  @media #{$small-only} {
    max-height: rem-calc(217);
  }

  .element-title {
    border: 1px solid $formfield-border;
    padding: rem-calc(7) rem-calc(15);

    @media #{$small-only} {
      text-align: center;
    }
  }

  .images-container {
    position: relative;

    .desktop-image-container {
      height: rem-calc(173);
      background-position: center top;
      background-repeat: no-repeat;
      background-size: cover;
    }

    .mobile-image-container {
      height: rem-calc(123);
      background-position: center top;
      background-repeat: no-repeat;
      background-size: cover;
    }

    .thumbnail-container {
      width: rem-calc(74);
      position: absolute;
      left: rem-calc(13);
      top: rem-calc(13);
    }
  }

  .link-row {
    padding: 0 rem-calc(10);

    a {
      padding: rem-calc(10) 0;
      border: 1px solid $formfield-border;
      border-radius: 0;
      text-decoration: none;
      background-color: $medium-light-gray;
      height: rem-calc(44);

      &:hover {
        background-color: $formfield-border;
        span {
          margin-left: rem-calc(10);
        }
      }

      &.secondary-link {
        color: $disturber-color;
        border: 1px solid $formfield-border;
        background-color: $white;
        border-right: 0;
      }

      .primary-link-text {
        color: $disturber-color;
      }

      p {
        margin-bottom: 0;
      }

      span {
        margin-left: rem-calc(5);
      }
    }
  }
}
</style>
